import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';
import { StockService } from '../../services/stock.service';
import { UpdateStockQuantityRequest } from '../../interfaces/stock-edit.interface';
import { StoreStockLot } from '../../interfaces/stock.interface';

@Component({
  selector: 'app-stock-quantity-form',
  templateUrl: './stock-quantity-form.component.html',
  styleUrls: ['./stock-quantity-form.component.scss']
})
export class StockQuantityFormComponent implements OnInit, OnDestroy {
  @Input() stockData!: StoreStockLot;
  @Output() formSubmitted = new EventEmitter<void>();
  @Output() stepBackEmitter = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();

  quantityForm: FormGroup;
  isLoading = false;

  reasonOptions = [
    { value: 3, label: 'pages.stock.edit.reasons.loss_damage' },
    { value: 2, label: 'pages.stock.edit.reasons.inventory_adjustment' },
    { value: 1, label: 'pages.stock.edit.reasons.direct_sale' }
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly stockService: StockService,
    private readonly translate: TranslateService,
    private readonly snackbarService: SnackbarService
  ) {
    this.quantityForm = this.createForm();
  }

  ngOnInit(): void {
    this.quantityForm.patchValue({
      newQuantity: this.stockData.stockQuantity,
      locations: this.stockData.locations
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      newQuantity: [0, [Validators.required, Validators.min(0)]],
      reason: ['', Validators.required],
      notes: [''],
      locations: [['']]
    });
  }

  get newQuantity(): number {
    return this.quantityForm.get('newQuantity')?.value || 0;
  }

  get quantityDifference(): number {
    return this.newQuantity - this.stockData.stockQuantity;
  }

  get isDifferencePositive(): boolean {
    return this.quantityDifference > 0;
  }

  get isDifferenceNegative(): boolean {
    return this.quantityDifference < 0;
  }

  onSubmit(): void {
    if (this.quantityForm.valid && !this.isLoading) {
      const formValue = this.quantityForm.value;

      if (formValue.newQuantity === this.stockData.stockQuantity) {
        this.snackbarService.showWarning(
          this.translate.instant('pages.stock.edit.no_change_warning')
        );
        return;
      }

      this.isLoading = true;

      const request: UpdateStockQuantityRequest = {
        quantity: formValue.newQuantity,
        reason: Number(formValue.reason),
        notes: formValue.notes || undefined,
        locations: formValue.locations
      };

      // this.stockService.updateLotQuantity(this.stockData.lotId, request)
      //   .pipe(takeUntil(this.destroy$))
      //   .subscribe({
      //     next: () => {
      //       this.isLoading = false;
      //       this.formSubmitted.emit();
      //     },
      //     error: (error) => {
      //       console.error('Error updating stock quantity:', error);
      //       this.isLoading = false;
      //     }
      //   });
    } else {
      this.quantityForm.markAllAsTouched();
    }
  }

  increaseQuantity(): void {
    const currentValue = this.newQuantity;
    this.quantityForm.patchValue({
      newQuantity: currentValue + 1
    });
  }

  decreaseQuantity(): void {
    const currentValue = this.newQuantity;
    if (currentValue > 0) {
      this.quantityForm.patchValue({
        newQuantity: currentValue - 1
      });
    }
  }

  goBack(): void {
    this.stepBackEmitter.emit();
  }
} 